import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { normalizeColor, useTresContext } from '@tresjs/core'
import { Vector2, Vector3, Plane } from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial'
import { computed, onUnmounted, shallowRef, watch } from 'vue'
import type { TresColor } from '@tresjs/core'
import type { Color } from 'three'

type Points = (Vector3 | Vector2 | [number, number, number] | [number, number] | number)[]
type VertexColors = Array<TresColor>
export interface LineProps {
  points: Points
  vertexColors?: VertexColors | null
  color?: TresColor
  lineWidth?: number
  worldUnits?: boolean
  alphaToCoverage?: boolean
  dashed?: boolean
  dashSize?: number
  gapSize?: number
  dashScale?: number
  dashOffset?: number,
  // #####
  clippingPlanes?: Plane[] | null
  // #####
}

type PropsType = typeof props


export default /*@__PURE__*/_defineComponent({
  __name: 'Line2FromCientosPackageAndItsTheSameButYouCanAlsoUseClippingPlanesSoItsNot',
  props: {
    points: {},
    vertexColors: { default: null },
    color: { default: 'white' },
    lineWidth: { default: 1 },
    worldUnits: { type: Boolean, default: false },
    alphaToCoverage: { type: Boolean, default: false },
    dashed: { type: Boolean, default: false },
    dashSize: { default: 1 },
    gapSize: { default: 1 },
    dashScale: { default: 1 },
    dashOffset: { default: 0 },
    clippingPlanes: { default: null }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props

function getInterpolatedVertexColors(vertexColors: VertexColors | null, numPoints: number): Color[] {
  if (!vertexColors || vertexColors.length === 0) {
    return Array.from({ length: numPoints }).fill(normalizeColor(props.color)) as Color[]
  }
  if (vertexColors.length === 1) {
    return Array.from({ length: numPoints }).fill(normalizeColor(vertexColors[0])) as Color[]
  }
  if (vertexColors.length === numPoints) {
    return vertexColors.map(normalizeColor)
  }

  const numSegments = numPoints - 1
  const mappedColors = vertexColors.map(normalizeColor)
  if (closed) { mappedColors.push(mappedColors[0].clone()) }

  const iColors: Color[] = [mappedColors[0]]
  const divisions = numSegments / (mappedColors.length - 1)
  for (let i = 1; i < numSegments; i++) {
    const alpha = (i % divisions) / divisions
    const colorIndex = Math.floor(i / divisions)
    iColors.push(mappedColors[colorIndex].clone().lerp(mappedColors[colorIndex + 1], alpha))
  }
  iColors.push(mappedColors[mappedColors.length - 1])

  return iColors
}

const lineMaterial = new LineMaterial()
const lineGeometry = new LineGeometry()
const line = new Line2(lineGeometry, lineMaterial)
const { sizes, invalidate } = useTresContext()
const hasVertexColors = computed(() => Array.isArray(props.vertexColors))

function updateLineMaterial(material: LineMaterial, props: PropsType) {
  material.color = normalizeColor(props.color)
  material.linewidth = props.lineWidth
  material.alphaToCoverage = props.alphaToCoverage
  material.worldUnits = props.worldUnits
  material.vertexColors = Array.isArray(props.vertexColors)
  material.dashed = props.dashed
  material.dashScale = props.dashScale
  material.dashSize = props.dashSize
  material.dashOffset = props.dashOffset
  material.gapSize = props.gapSize
  material.needsUpdate = true
  // #####
  material.clippingPlanes = props.clippingPlanes
  // #####
}

function updateLineGeometry(geometry: LineGeometry, points: Points, vertexColors: VertexColors | null) {
  const pValues = points.map((p) => {
    if (p instanceof Vector3) {
      return [p.x, p.y, p.z]
    }
    else if (p instanceof Vector2) {
      return [p.x, p.y, 0]
    }
    else if (Array.isArray(p) && p.length === 2) {
      return [p[0], p[1], 0]
    }
    else {
      return p
    }
  }).flat()
  geometry.setPositions(pValues.flat())

  const colors = getInterpolatedVertexColors(vertexColors, points.length).map(c => c.toArray()).flat()
  geometry.setColors(colors)

  line.computeLineDistances()
}

updateLineMaterial(lineMaterial, props)
updateLineGeometry(lineGeometry, props.points, props.vertexColors)
line.computeLineDistances()

watch(() => [
  props.color,
  props.lineWidth,
  props.alphaToCoverage,
  props.worldUnits,
  hasVertexColors,
  props.dashed,
  props.dashScale,
  props.dashSize,
  props.dashOffset,
  // #####
  props.clippingPlanes
  // #####
], () => {
  updateLineMaterial(lineMaterial, props)
  invalidate()
})
watch(() => [props.points, props.vertexColors], () => {
  updateLineGeometry(lineGeometry, props.points, props.vertexColors)
  invalidate()
})
watch(() => [sizes.height, sizes.width], () => {
  lineMaterial.resolution = new Vector2(sizes.width.value, sizes.height.value)
  invalidate()
})

onUnmounted(() => {
  lineGeometry.dispose()
  lineMaterial.dispose()
})

const lineRef = shallowRef()
__expose({ instance: lineRef })

return (_ctx: any,_cache: any) => {
  const _component_primitive = _resolveComponent("primitive")!

  return (_openBlock(), _createBlock(_component_primitive, {
    ref: lineRef.value,
    object: _unref(line)
  }, null, 8, ["object"]))
}
}

})